import { useCallback, useEffect, useRef } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';

interface FormStoreSyncOptions<T extends FieldValues = FieldValues> {
	form: UseFormReturn<T>;
	updateStore: (values: T) => void;
	debounceMs?: number;
}

export const useFormStoreSync = <T extends FieldValues = FieldValues>({
	form,
	updateStore,
	debounceMs = 500,
}: FormStoreSyncOptions<T>) => {
	const timeoutRef = useRef<NodeJS.Timeout>();
	const previousValuesRef = useRef<T>({} as T);

	const debouncedUpdate = useCallback(
		(values: T) => {
			// Check if values actually changed to prevent unnecessary updates
			const hasChanged = Object.keys(values).some((key) => {
				const newValue = values[key];
				const oldValue = previousValuesRef.current[key];
				return newValue !== oldValue;
			});

			if (!hasChanged) return;

			previousValuesRef.current = { ...values };
			updateStore(values);
		},
		[updateStore]
	);

	useEffect(() => {
		const subscription = form.watch((values) => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			timeoutRef.current = setTimeout(() => {
				debouncedUpdate(values as T);
			}, debounceMs);
		});

		return () => {
			subscription.unsubscribe();
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [form, debouncedUpdate, debounceMs]);
};
